import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AuthCard, AuthMessage, PasswordRules, passwordMeetsRules } from './SignInScreens';
import { setPortalSession } from './portal/portalApi';

// The Customer portal's Set password screen (issue #253), opened from the
// link in a Customer login's invite email (/portal/set-password?token=...)
// or a Forgot password reset email (/portal/reset-password?token=...,
// issue #256). Saving signs the Customer straight in and lands them on
// their portal's welcome page (issue #254, portal/PortalApp.js). Served
// ahead of the staff sign-in, since nobody is signed in when it opens.

const INPUT_STYLE = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, boxSizing: 'border-box' };
const PRIMARY_BUTTON = { padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, fontSize: 13 };
const LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };

export const PORTAL_SET_PASSWORD_PATH = '/portal/set-password';
export const PORTAL_RESET_PASSWORD_PATH = '/portal/reset-password';

function errorMessage(err, fallback) {
  if (!err.response) return 'Could not reach the server. It may be starting up, so try again in a moment.';
  return err.response.data?.message || fallback;
}

function PortalSetPasswordScreen() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [link, setLink] = useState(null); // { purpose, email, customer_name }
  const [linkError, setLinkError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios.get(`/api/portal-auth/links/${encodeURIComponent(token)}`)
      .then((res) => { if (!cancelled) setLink(res.data.data); })
      .catch((err) => { if (!cancelled) setLinkError(errorMessage(err, 'This link isn\'t valid.')); });
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('The two passwords don\'t match.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await axios.post('/api/portal-auth/set-password', { token, password });
      const { token: session, portal_name: portalName } = res.data.data;
      setPortalSession(portalName, session);
      window.location.replace(`/portal/${encodeURIComponent(portalName)}`);
      return;
    } catch (err) {
      setError(errorMessage(err, 'Could not set your password'));
    }
    setSaving(false);
  };

  if (linkError) {
    return (
      <AuthCard portal subtitle="This link can't be used">
        <div style={{ fontSize: 14 }}>{linkError}</div>
      </AuthCard>
    );
  }
  if (!link) return <AuthMessage>Checking your link...</AuthMessage>;
  const isReset = link.purpose === 'reset';

  return (
    <AuthCard portal subtitle={`Choose a${isReset ? ' new' : ''} password for ${link.email}${link.customer_name ? ` (${link.customer_name})` : ''}.`}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input type="email" autoComplete="username" value={link.email} readOnly hidden />
        <div>
          <label style={LABEL_STYLE}>New password</label>
          <input autoFocus type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} style={INPUT_STYLE} />
          <PasswordRules password={password} />
        </div>
        <div>
          <label style={LABEL_STYLE}>Confirm new password</label>
          <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} style={INPUT_STYLE} />
        </div>
        {error && <div style={{ color: 'red', fontSize: 13 }}>{error}</div>}
        <button type="submit" disabled={saving || !passwordMeetsRules(password) || !confirm} style={{ ...PRIMARY_BUTTON, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? 'Saving...' : `${isReset ? 'Save new' : 'Set'} password and sign in`}
        </button>
      </form>
    </AuthCard>
  );
}

export default PortalSetPasswordScreen;
